import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePlus2, CalendarPlus, CheckCircle2, Ban } from "lucide-react";
import { crm } from "./crmApi";
import { Table, Pill, statusTone, Modal, Field, Area, Sel, Loading, Err, fmtDate, fmtMoney, fmtTime } from "./ui";
import { Button } from "@/components/ui/Button";

const EST_STATUSES = ["Draft", "Sent", "Pending", "Approved", "Declined", "Expired"];
const JOB_STATUSES = ["New", "Estimate Pending", "Estimate Approved", "Scheduled", "In Progress", "Completed", "Cancelled", "Warranty"];

const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { crm("listCustomers").then(r => setCustomers(r.customers)).catch(() => {}); }, []);
  return customers;
};

// ---------- ESTIMATES ----------
const EMPTY_EST = { customerId: "", description: "", laborCost: "", materialCost: "", depositRequired: "", notes: "" };

export const EstimatesTab = () => {
  const [estimates, setEstimates] = useState(null);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [convert, setConvert] = useState(null); // estimate being converted
  const [sched, setSched] = useState({ date: "", startTime: "09:00", endTime: "10:00", address: "", title: "" });
  const [form, setForm] = useState(EMPTY_EST);
  const customers = useCustomers();

  const load = async () => {
    setError(null);
    try { const r = await crm("listEstimates"); setEstimates(r.estimates.reverse()); }
    catch (e) { setError(e); setEstimates([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await crm("createEstimate", form);
      toast.success(`Estimate ${r.estimateNumber} created — total ${fmtMoney(r.total)}`);
      setShowNew(false); setForm(EMPTY_EST); load();
    } catch (err) { toast.error(err.message); }
  };

  const setStatus = async (estimateId, status) => {
    try {
      const r = await crm("updateEstimate", { estimateId, status });
      toast.success(
        status === "Sent"
          ? (r && r.emailed ? "Estimate sent & emailed to customer (they reply APPROVE to accept)" : "Estimate marked Sent — no customer email on file")
          : `Estimate ${status}`
      );
      load();
    } catch (e) { toast.error(e.message); }
  };

  const convertToJob = async (e) => {
    e.preventDefault();
    try {
      const r = await crm("scheduleJob", {
        customerId: convert["Customer ID"], estimateNumber: convert["Estimate Number"],
        date: sched.date, startTime: sched.startTime, endTime: sched.endTime,
        address: sched.address, title: sched.title || convert["Description"],
      });
      toast.success(`Job ${r.jobId} scheduled & added to Google Calendar`);
      setConvert(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const total = (parseFloat(form.laborCost) || 0) + (parseFloat(form.materialCost) || 0);

  if (estimates === null) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="new-estimate-btn" size="sm" onClick={() => setShowNew(true)}><FilePlus2 size={16} /> Create Estimate</Button>
      </div>
      {error ? <Err error={error} /> : (
        <Table
          testId="estimates-table"
          cols={[
            { key: "Estimate Number", label: "Estimate" },
            { key: "Customer ID", label: "Customer" },
            { key: "Description", label: "Description", render: r => <span className="line-clamp-2 max-w-[240px] inline-block">{r["Description"]}</span> },
            { key: "Total Estimate", label: "Total", render: r => fmtMoney(r["Total Estimate"]) },
            { key: "Deposit Required", label: "Deposit", render: r => fmtMoney(r["Deposit Required"]) },
            { key: "Estimate Status", label: "Status", render: r => (
              <span onClick={e => e.stopPropagation()} className="flex items-center gap-2">
                <Pill tone={statusTone(r["Estimate Status"])}>{r["Estimate Status"]}</Pill>
                <Sel label="" options={EST_STATUSES} value={r["Estimate Status"]}
                  onChange={e => setStatus(r["Estimate Number"], e.target.value)} />
              </span>
            )},
            { key: "Job ID", label: "Job", render: r => r["Job ID"] || (r["Estimate Status"] === "Approved" ? (
              <Button size="sm" variant="outline" onClick={() => setConvert(r)}><CalendarPlus size={14} /> To Job</Button>
            ) : "—") },
          ]}
          rows={estimates}
        />
      )}

      {showNew && (
        <Modal title="Create Estimate" onClose={() => setShowNew(false)} testId="new-estimate-modal">
          <form onSubmit={create} className="space-y-4">
            <Sel label="Customer *" required options={[{ value: "", label: "Select customer" }, ...customers.map(c => ({ value: c["Customer ID"], label: `${c["First Name"]} ${c["Last Name"]}`.trim() || c["Customer ID"] }))]}
              value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })} />
            <Area label="Description *" required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Labor Cost" type="number" min="0" step="0.01" value={form.laborCost} onChange={e => setForm({ ...form, laborCost: e.target.value })} />
              <Field label="Material Cost" type="number" min="0" step="0.01" value={form.materialCost} onChange={e => setForm({ ...form, materialCost: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Deposit Required" type="number" min="0" step="0.01" value={form.depositRequired} onChange={e => setForm({ ...form, depositRequired: e.target.value })} />
              <div className="pb-2 text-white/60 text-sm">Total: <span data-testid="estimate-total" className="text-weld-glow font-bold">{fmtMoney(total)}</span></div>
            </div>
            <Area label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Button data-testid="create-estimate-submit" type="submit" className="w-full">Create Estimate</Button>
          </form>
        </Modal>
      )}

      {convert && (
        <Modal title={`Schedule Job from ${convert["Estimate Number"]}`} onClose={() => setConvert(null)} testId="convert-modal">
          <form onSubmit={convertToJob} className="space-y-4">
            <Field label="Job Title" value={sched.title} onChange={e => setSched({ ...sched, title: e.target.value })} placeholder={convert["Description"]} />
            <Field label="Date *" type="date" required value={sched.date} onChange={e => setSched({ ...sched, date: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start Time" type="time" value={sched.startTime} onChange={e => setSched({ ...sched, startTime: e.target.value })} />
              <Field label="End Time" type="time" value={sched.endTime} onChange={e => setSched({ ...sched, endTime: e.target.value })} />
            </div>
            <Field label="Address / Location" value={sched.address} onChange={e => setSched({ ...sched, address: e.target.value })} />
            <Button data-testid="convert-submit" type="submit" className="w-full">Schedule Job + Add to Calendar</Button>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------- JOBS ----------
const EMPTY_JOB = { customerId: "", estimateNumber: "", title: "", description: "", serviceType: "", address: "", date: "", startTime: "09:00", endTime: "10:00", totalPrice: "", notes: "" };

export const JobsTab = () => {
  const [jobs, setJobs] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_JOB);
  const customers = useCustomers();

  const load = async () => {
    setError(null);
    try {
      const r = await crm("listJobs");
      setJobs(r.jobs.reverse());
      crm("listEstimates").then(x => setEstimates(x.estimates.filter(e => e["Estimate Status"] === "Approved" && !e["Job ID"]))).catch(() => {});
    } catch (e) { setError(e); setJobs([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await crm("scheduleJob", form);
      toast.success(`Job ${r.jobId} scheduled & added to Google Calendar`);
      setShowNew(false); setForm(EMPTY_JOB); load();
    } catch (err) { toast.error(err.message); }
  };

  const setStatus = async (jobId, status) => {
    try { await crm("updateJob", { jobId, status }); toast.success(`Job ${status}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  const complete = async (jobId) => {
    try {
      const r = await crm("completeJob", { jobId });
      toast.success(`Job completed — warranty ${r.warrantyId} + ${r.followUpsCreated} follow-ups created${r.emailed ? " + customer thank-you email sent" : ""}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (jobs === null) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="schedule-job-btn" size="sm" onClick={() => setShowNew(true)}><CalendarPlus size={16} /> Schedule Job</Button>
      </div>
      {error ? <Err error={error} /> : (
        <Table
          testId="jobs-table"
          cols={[
            { key: "Job ID", label: "Job" },
            { key: "Customer ID", label: "Customer" },
            { key: "Job Title", label: "Title", render: r => <span className="line-clamp-2 max-w-[200px] inline-block">{r["Job Title"]}</span> },
            { key: "Scheduled Date", label: "When", render: r => `${fmtDate(r["Scheduled Date"])} ${fmtTime(r["Start Time"])}` },
            { key: "Job Status", label: "Status", render: r => (
              <span onClick={e => e.stopPropagation()} className="flex items-center gap-2">
                <Pill tone={statusTone(r["Job Status"])}>{r["Job Status"]}</Pill>
                <Sel label="" options={JOB_STATUSES} value={r["Job Status"]} onChange={e => setStatus(r["Job ID"], e.target.value)} />
              </span>
            )},
            { key: "Total Price", label: "Price", render: r => fmtMoney(r["Total Price"]) },
            { key: "Remaining Balance", label: "Balance", render: r => <span className={parseFloat(r["Remaining Balance"]) > 0 ? "text-amber-400" : "text-weld-glow"}>{fmtMoney(r["Remaining Balance"])}</span> },
            { key: "Calendar Event ID", label: "Calendar", render: r => r["Calendar Event ID"] ? <Pill tone="green">Synced</Pill> : <Pill>—</Pill> },
            { key: "actions", label: "Actions", render: r => (
              <span onClick={e => e.stopPropagation()} className="flex gap-2">
                {!["Completed", "Cancelled"].includes(r["Job Status"]) && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => complete(r["Job ID"])}><CheckCircle2 size={14} /> Complete</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus(r["Job ID"], "Cancelled")}><Ban size={14} /></Button>
                  </>
                )}
              </span>
            )},
          ]}
          rows={jobs}
        />
      )}

      {showNew && (
        <Modal title="Schedule Job" onClose={() => setShowNew(false)} testId="schedule-job-modal">
          <form onSubmit={create} className="space-y-4">
            <Sel label="Customer *" required options={[{ value: "", label: "Select customer" }, ...customers.map(c => ({ value: c["Customer ID"], label: `${c["First Name"]} ${c["Last Name"]}`.trim() || c["Customer ID"] }))]}
              value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })} />
            <Sel label="From Approved Estimate (optional)" options={[{ value: "", label: "None" }, ...estimates.filter(e => !form.customerId || e["Customer ID"] === form.customerId).map(e2 => ({ value: e2["Estimate Number"], label: `${e2["Estimate Number"]} — ${fmtMoney(e2["Total Estimate"])}` }))]}
              value={form.estimateNumber} onChange={e => setForm({ ...form, estimateNumber: e.target.value })} />
            <Field label="Job Title *" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <Area label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Service Type" value={form.serviceType} onChange={e => setForm({ ...form, serviceType: e.target.value })} />
              <Field label="Total Price" type="number" min="0" step="0.01" value={form.totalPrice} onChange={e => setForm({ ...form, totalPrice: e.target.value })} />
            </div>
            <Field label="Date *" type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start Time" type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
              <Field label="End Time" type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
            </div>
            <Field label="Address / Location" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            <Area label="Job Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Button data-testid="schedule-job-submit" type="submit" className="w-full">Schedule Job + Add to Calendar</Button>
          </form>
        </Modal>
      )}
    </div>
  );
};
